import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import Transactions from './pages/Transactions';
import Users from './pages/Users';
import UserDetail from './pages/UserDetail';
import Booking from './pages/Booking';
import Payments from './pages/Payments';
import Restaurants from './pages/Restaurants';
import Coupons from './pages/Coupons';
import Notifications from './pages/Notifications';
import Settings from './pages/Settings';
import LegalDocument from './pages/LegalDocument';
import Support from './pages/Support';
import CsaePolicy from './pages/CsaePolicy';
// import LegalEditor from './pages/LegalEditor';
import Trips from './pages/Trips';
import TripDetail from './pages/TripDetail';
import Feedback from './pages/Feedback';
import Faqs from './pages/Faqs';
import UtmTracking from './pages/UtmTracking';
import MainEvents from './pages/MainEvents';
import GiftCardRedemptions from './pages/GiftCardRedemptions';
import GiftCardRedemptionDetail from './pages/GiftCardRedemptionDetail';
import GiftCardProviderSettings from './pages/GiftCardProviderSettings';
import Reports from './pages/Reports';
import './App.css';

// Public routes accessible without authentication.
const publicPaths = ['/privacypolicy', '/termsandservices', '/cookiespolicy', '/support', '/csaepolicy'];

const pageTitles = {
  '/dashboard': { title: 'Dashboard', subtitle: "Welcome back, Admin! Here's what's happening with Delmont." },
  '/transactions': { title: 'Transactions', subtitle: 'View petal transactions across all users.' },
  '/users': { title: 'User Management', subtitle: "Welcome back, Admin! Here's what's happening with Delmont." },
  '/users/active': { title: 'User Management', subtitle: "Welcome back, Admin! Here's what's happening with Delmont." },
  '/users/inactive': { title: 'User Management', subtitle: "Welcome back, Admin! Here's what's happening with Delmont." },
  '/users/pending': { title: 'User Management', subtitle: "Welcome back, Admin! Here's what's happening with Delmont." },
  '/users/blocked': { title: 'User Management', subtitle: "Welcome back, Admin! Here's what's happening with Delmont." },
  '/booking': { title: 'Booking Management', subtitle: "Welcome back, Admin! Here's what's happening with Delmont." },
  '/restaurants': { title: 'Partner Venue', subtitle: 'Manage and organise all partner venues on the platform' },
  '/trips': { title: 'Trip', subtitle: 'Manage and organise all trips on the platform' },
  '/main-events': { title: 'Main Events', subtitle: 'Browse the main events calendar and import it from a spreadsheet.' },
  '/payments': { title: 'Payment Management', subtitle: "Welcome back, Admin! Here's what's happening with Delmont." },
  '/gift-card-redemptions': { title: 'Gift Card Redemptions', subtitle: 'Review gift-card orders that need a decision and track every Xoxoday attempt.' },
  '/gift-card-provider-settings': { title: 'Gift Card Provider Settings', subtitle: 'Manage the Xoxoday TEST and LIVE accounts and choose which one gift cards use.' },
  '/reports': { title: 'Reports', subtitle: 'Review users reported by other members and decide whether to block them.' },
  '/notifications': { title: 'Notifications', subtitle: "Welcome back, Admin! Here's what's happening with Delmont." },
  '/feedback': { title: 'Feedback', subtitle: 'Review user ratings and feedback submitted from the app.' },
  '/faqs': { title: 'FAQ', subtitle: 'Create and manage frequently asked questions shown in the app.' },
  '/utm-tracking': { title: 'UTM Tracking', subtitle: 'Track VIP membership button taps with user and device details.' },
  '/settings': { title: 'Settings', subtitle: "Welcome back, Admin! Here's what's happening with Delmont." },
  // '/legal': { title: 'Legal Documents', subtitle: 'Edit the Privacy Policy, Terms of Service, and Cookies Policy shown in the app.' },
};

function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, loading } = useAuth();
  const location = useLocation();
  const currentPage = pageTitles[location.pathname]
    || (location.pathname.match(/^\/users\/[^/]+$/) && !['active', 'inactive', 'pending', 'blocked'].includes(location.pathname.split('/').pop()) ? { title: 'User Details', subtitle: "Welcome back, Admin! Here's what's happening with Delmont." } : null)
    || (location.pathname.match(/^\/restaurants\/[^/]+\/coupons$/) ? { title: 'Partner Venue Details', subtitle: 'View partner venue information and manage issued coupons' } : null)
    || (location.pathname.match(/^\/trips\/[^/]+$/) ? { title: 'Trip Details', subtitle: 'View trip details and applicant status' } : null)
    || (location.pathname.match(/^\/gift-card-redemptions\/[^/]+$/) ? { title: 'Gift Card Redemption Details', subtitle: 'Review the order, its retry history, and approve or reject it' } : null)
    || pageTitles['/dashboard'];

  // Public pages — render standalone, regardless of auth state.
  if (publicPaths.includes(location.pathname)) {
    return (
      <Routes>
        <Route
          path="/privacypolicy"
          element={<LegalDocument type="privacy_policy" defaultTitle="Privacy Policy" />}
        />
        <Route
          path="/termsandservices"
          element={<LegalDocument type="terms_of_service" defaultTitle="Terms of Service" />}
        />
        <Route
          path="/cookiespolicy"
          element={<LegalDocument type="cookies_policy" defaultTitle="Cookies Policy" />}
        />
        <Route path="/support" element={<Support />} />
        <Route path="/csaepolicy" element={<CsaePolicy />} />
      </Routes>
    );
  }

  // Wait for localStorage session restore so refresh keeps the current route
  // instead of bouncing through /login → /dashboard.
  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="main-content">
        
        <main className="page-content">
          <Navbar
          onMenuClick={() => setSidebarOpen(true)}
          pageTitle={currentPage.title}
          pageSubtitle={currentPage.subtitle}
        />
          <Routes>
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
            <Route path="/users/active" element={<ProtectedRoute><Users /></ProtectedRoute>} />
            <Route path="/users/inactive" element={<ProtectedRoute><Users /></ProtectedRoute>} />
            <Route path="/users/pending" element={<ProtectedRoute><Users /></ProtectedRoute>} />
            <Route path="/users/blocked" element={<ProtectedRoute><Users /></ProtectedRoute>} />
            <Route path="/users/:userId" element={<ProtectedRoute><UserDetail /></ProtectedRoute>} />
            <Route path="/booking" element={<ProtectedRoute><Booking /></ProtectedRoute>} />
            <Route path="/restaurants" element={<ProtectedRoute><Restaurants /></ProtectedRoute>} />
            <Route path="/restaurants/:restaurantId/coupons" element={<ProtectedRoute><Coupons /></ProtectedRoute>} />
            <Route path="/trips" element={<ProtectedRoute><Trips /></ProtectedRoute>} />
            <Route path="/trips/:tripId" element={<ProtectedRoute><TripDetail /></ProtectedRoute>} />
            <Route path="/main-events" element={<ProtectedRoute><MainEvents /></ProtectedRoute>} />
            <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
            <Route path="/gift-card-redemptions" element={<ProtectedRoute><GiftCardRedemptions /></ProtectedRoute>} />
            <Route path="/gift-card-redemptions/:redemptionId" element={<ProtectedRoute><GiftCardRedemptionDetail /></ProtectedRoute>} />
            <Route path="/gift-card-provider-settings" element={<ProtectedRoute><GiftCardProviderSettings /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
            <Route path="/feedback" element={<ProtectedRoute><Feedback /></ProtectedRoute>} />
            <Route path="/faqs" element={<ProtectedRoute><Faqs /></ProtectedRoute>} />
            <Route path="/utm-tracking" element={<ProtectedRoute><UtmTracking /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            {/* <Route path="/legal" element={<ProtectedRoute><LegalEditor /></ProtectedRoute>} /> */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter basename={process.env.PUBLIC_URL}>
      <AuthProvider>
        <NotificationProvider>
          <AppLayout />
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
